import { StringCompare } from '@stamhoofd/utility';
import { assertFilterCompareValue, compileFilter, filterDefinitionsToSelector } from './FilterCompilers.js';
function $andInMemoryFilterCompiler(filter, filters) {
    const runners = compileInMemoryFilter(filter, filters);
    return (object) => {
        for (const runner of runners) {
            if (!runner(object)) {
                return false;
            }
        }
        return true;
    };
}
function $orInMemoryFilterCompiler(filter, filters) {
    const runners = compileInMemoryFilter(filter, filters);
    return (object) => {
        for (const runner of runners) {
            if (runner(object)) {
                return true;
            }
        }
        return false;
    };
}
function $notInMemoryFilterCompiler(filter, filters) {
    const andRunner = $andInMemoryFilterCompiler(filter, filters);
    return (object) => {
        return !andRunner(object);
    };
}
function $lessThanInMemoryFilterCompiler(filter) {
    return (val) => {
        const a = normalizeValue(assertFilterCompareValue(val));
        if (a === null) {
            return true;
        }
        const b = normalizeValue(assertFilterCompareValue(filter));
        if (b === null) {
            return false;
        }
        return a < b;
    };
}
function $greaterThanInMemoryFilterCompiler(filter) {
    return (val) => {
        const a = normalizeValue(assertFilterCompareValue(val));
        if (a === null) {
            return false;
        }
        const b = normalizeValue(assertFilterCompareValue(filter));
        if (b === null) {
            return true;
        }
        return a > b;
    };
}
function $equalsInMemoryFilterCompiler(filter) {
    return (val) => {
        const b = normalizeValue(assertFilterCompareValue(filter));
        // in the backend, if a property does not exist and comparing against null, the result is true (if column type JSONArray)
        // if not comparing against a json column the value can never be undefined in the backend (would be null)
        if (b === null && (val === null || val === undefined)) {
            return true;
        }
        if (Array.isArray(val)) {
            // To match backend logic where these things are required for optimizations
            // + also match MongoDB behavior
            // in the backend comparing null against an empty array returns true
            if (b === null && val.length === 0) {
                return true;
            }
            for (const v of val) {
                const a = normalizeValue(assertFilterCompareValue(v));
                if (a === b) {
                    return true;
                }
            }
            return false;
        }
        const a = normalizeValue(assertFilterCompareValue(val));
        return a === b;
    };
}
function invertFilterCompiler(compiler) {
    return (filter, compilers) => {
        const runner = compiler(filter, compilers);
        return (val) => {
            return !runner(val);
        };
    };
}
function $containsInMemoryFilterCompiler(filter) {
    return (val) => {
        const needle = normalizeValue(assertFilterCompareValue(filter));
        if (typeof needle !== 'string') {
            return false;
        }
        if (Array.isArray(val)) {
            // To match backend logic where these things are required for optimizations
            // + also match MongoDB behavior
            for (const v of val) {
                const a = normalizeValue(assertFilterCompareValue(v));
                if (typeof a === 'string' && StringCompare.contains(a, needle)) {
                    return true;
                }
            }
            return false;
        }
        const a = normalizeValue(assertFilterCompareValue(val));
        if (typeof a !== 'string') {
            return false;
        }
        return StringCompare.contains(a, needle);
    };
}
function $inInMemoryFilterCompiler(filter) {
    return (val) => {
        if (val === undefined) {
            // Using $in on a property that does not exist should always return false
            return false;
        }
        if (!Array.isArray(filter)) {
            throw new Error('Invalid filter: expected array as value for $in filter');
        }
        if (Array.isArray(val)) {
            // To match backend logic (JSON_OVERLAPS in MySQL) where these things are required for optimizations
            // + also match MongoDB behavior
            for (const v of val) {
                const a = normalizeValue(assertFilterCompareValue(v));
                for (const element of filter) {
                    const b = normalizeValue(assertFilterCompareValue(element));
                    if (a === b) {
                        return true;
                    }
                }
            }
            return false;
        }
        const a = normalizeValue(assertFilterCompareValue(val));
        for (const element of filter) {
            const b = normalizeValue(assertFilterCompareValue(element));
            if (a === b) {
                return true;
            }
        }
        return false;
    };
}
function $elemMatchInMemoryFilterCompiler(filter, compilers) {
    const runner = $andInMemoryFilterCompiler(filter, compilers);
    return (val) => {
        if (val === undefined) {
            // Using $elemMatch on a property that does not exist should always return false
            return false;
        }
        if (!Array.isArray(val)) {
            throw new Error('Invalid filter: expected array as value for $elemMatch filter');
        }
        for (const o of val) {
            // Check if individual item matches the filter
            if (runner(o)) {
                return true;
            }
        }
        return false;
    };
}
function $lengthInMemoryFilterCompiler(filter, compilers) {
    const runner = $andInMemoryFilterCompiler(filter, compilers);
    return (val) => {
        if (typeof val === 'string' || Array.isArray(val)) {
            return runner(val.length);
        }
        throw new Error('Invalid filter: expected string or array as value for $length filter');
    };
}
function objectPathValue(object, path) {
    if (path.length === 0) {
        return object;
    }
    const nextSearched = path[0];
    if (object instanceof Map) {
        if (object.has(nextSearched)) {
            return objectPathValue(object.get(nextSearched), path.slice(1));
        }
        // This mimics SQL behaviour where you can add wildcards in paths, which expands to a JSONArray
        if (nextSearched === '*') {
            // Create an array with all the values of the map, following the path for each individual value
            const array = [];
            for (const value of object.values()) {
                array.push(objectPathValue(value, path.slice(1)));
            }
            return array;
        }
        return undefined;
    }
    if (typeof object !== 'object' || object === null) {
        return undefined;
    }
    if (nextSearched in object) {
        return objectPathValue(object[nextSearched], path.slice(1));
    }
    // This mimics SQL behaviour where you can add wildcards in paths, which expands to a JSONArray
    if (nextSearched === '*' && Array.isArray(object)) {
        // Create an array with all the values of the map, following the path for each individual value
        const array = [];
        for (const value of object) {
            array.push(objectPathValue(value, path.slice(1)));
        }
        return array;
    }
}
function normalizeValue(val) {
    if (val instanceof Date) {
        return val.getTime();
    }
    if (typeof val === 'string') {
        return val.toLocaleLowerCase();
    }
    if (typeof val === 'boolean') {
        return val === true ? 1 : 0;
    }
    if (val === null) {
        return null;
    }
    if (typeof val === 'object' && '$' in val) {
        const specialValue = val['$'];
        switch (specialValue) {
            case '$now':
                return normalizeValue(new Date());
            case '$rel': {
                return normalizeValue(val['value']);
            }
            default:
                throw new Error('Unsupported magic value ' + specialValue);
        }
    }
    return val;
}
export function createInMemoryFilterCompiler(path, overrideFilterDefinitions) {
    const splitted = Array.isArray(path) ? path : path.split('.');
    return (filter, compilers) => {
        const runner = $andInMemoryFilterCompiler(filter, overrideFilterDefinitions ? (typeof overrideFilterDefinitions === 'function' ? overrideFilterDefinitions : filterDefinitionsToSelector(overrideFilterDefinitions)) : filterDefinitionsToSelector(baseInMemoryFilterCompilers));
        return (object) => {
            const value = objectPathValue(object, splitted);
            if (value === undefined) {
                // Cannot filter on property that does not exists
                // (no need to continue here on the filters as these will throw on an undefined value)
                return false;
            }
            return runner(value);
        };
    };
}
export function createInMemoryWildcardCompilerSelector(overrideFilterDefinitions) {
    return (filter, parentCompiler, key) => {
        // Every key will match on this compiler
        const compiler = createInMemoryFilterCompiler(key, overrideFilterDefinitions);
        return compiler(filter, compiler, key);
    };
}
export const baseInMemoryFilterCompilers = {
    $and: $andInMemoryFilterCompiler,
    $or: $orInMemoryFilterCompiler,
    $not: $notInMemoryFilterCompiler,
    $eq: $equalsInMemoryFilterCompiler,
    $neq: invertFilterCompiler($equalsInMemoryFilterCompiler),
    $lt: $lessThanInMemoryFilterCompiler,
    $gt: $greaterThanInMemoryFilterCompiler,
    $lte: invertFilterCompiler($greaterThanInMemoryFilterCompiler),
    $gte: invertFilterCompiler($lessThanInMemoryFilterCompiler),
    $in: $inInMemoryFilterCompiler,
    $elemMatch: $elemMatchInMemoryFilterCompiler,
    $contains: $containsInMemoryFilterCompiler,
    $length: $lengthInMemoryFilterCompiler,
};
const compileInMemoryFilter = (compileFilter);
export const compileToInMemoryFilter = (filter, filters) => {
    if (filter === null) {
        return () => true;
    }
    return $andInMemoryFilterCompiler(filter, filterDefinitionsToSelector(filters));
};
//# sourceMappingURL=InMemoryFilter.js.map